import '@awesome.me/webawesome/dist/components/button/button.js';

import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  buildCurveFromExtraction,
  buildRecurrenceFromExtraction,
  extractItems,
} from '../services/extraction.js';
import type { CreateItemInput, ItemRepository } from '../services/index.js';
import { useUIStore } from '../stores/ui-store.js';

interface QuickAddProps {
  itemRepo: ItemRepository;
  onAdded: () => void;
}

export function QuickAdd({ itemRepo, onAdded }: QuickAddProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [processing, setProcessing] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const closeQuickAdd = useUIStore(s => s.closeQuickAdd);

  const handleSubmit = useCallback(async () => {
    if (input.trim().length === 0) return;
    setProcessing(true);
    setFeedback(null);

    try {
      const result = await extractItems({ mode: 'braindump', input: input.trim() });
      const ext = result.items[0];
      if (ext) {
        const itemInput: CreateItemInput = {
          text: ext.text,
          rawInput: input.trim(),
          source: 'quick-add',
          category: ext.category,
          consequence: ext.consequence,
          effort: ext.effort,
          curve: buildCurveFromExtraction(ext),
          recurrence: buildRecurrenceFromExtraction(ext),
        };
        await itemRepo.create(itemInput);
      } else {
        // AI returned nothing — add raw
        await itemRepo.create({
          text: input.trim(),
          rawInput: input.trim(),
          source: 'quick-add',
          curve: { direction: 'cooling', startingTemperature: 60, easing: 'ease-in', duration: 30 },
        });
      }
      setFeedback(t('capture:quickAdd.added'));
      setInput('');
      onAdded();
      setTimeout(() => {
        setFeedback(null);
        closeQuickAdd();
      }, 800);
    } catch {
      // Fallback: add raw
      await itemRepo.create({
        text: input.trim(),
        rawInput: input.trim(),
        source: 'quick-add',
        curve: { direction: 'cooling', startingTemperature: 60, easing: 'ease-in', duration: 30 },
      });
      setFeedback(t('capture:quickAdd.added'));
      setInput('');
      onAdded();
      setTimeout(() => {
        setFeedback(null);
        closeQuickAdd();
      }, 800);
    } finally {
      setProcessing(false);
    }
  }, [input, itemRepo, onAdded, closeQuickAdd, t]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        padding: 'var(--space-lg)',
        zIndex: 100,
      }}
      onClick={e => {
        if (e.target === e.currentTarget) closeQuickAdd();
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '480px',
          background: 'var(--color-bg)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-lg)',
          border: '1px solid var(--color-card-border)',
        }}
      >
        <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => {
              setInput(e.target.value);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') void handleSubmit();
              if (e.key === 'Escape') closeQuickAdd();
            }}
            placeholder={t('capture:quickAdd.placeholder')}
            disabled={processing}
            autoFocus
            style={{
              flex: 1,
              background: 'var(--color-card)',
              border: '1px solid var(--color-card-border)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-sm) var(--space-md)',
              color: 'var(--color-text-primary)',
              fontSize: '1rem',
            }}
          />
          <wa-button
            variant="brand"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={processing || input.trim().length === 0}
          >
            {processing ? t('capture:quickAdd.processing') : t('capture:quickAdd.submit')}
          </wa-button>
        </div>
        {feedback && (
          <p
            style={{
              color: 'var(--color-positive-text)',
              fontSize: '0.875rem',
              marginTop: 'var(--space-sm)',
              textAlign: 'center',
            }}
          >
            {feedback}
          </p>
        )}
      </div>
    </div>
  );
}
